import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  VideoConference,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { apiFetch } from "@/lib/api-client";

// ---------------------------------------------------------------------------
// G.22: Post-Webinar Networking
// ---------------------------------------------------------------------------
// Host controls: mode selector, config, start/stop, live dashboard.
// Participant view: opt-in gate, partner card, topic tables, exchange contact.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type NetworkingMode = "speed" | "topics" | "open_mixer";

interface NetworkingStatus {
  active: boolean;
  mode: string | null;
  participantCount: number;
  currentRound?: number;
  timeRemaining?: number;
  tables?: TableInfo[];
}

interface TableInfo {
  id: string;
  topic: string;
  participantCount: number;
  maxParticipants: number;
}

interface JoinAssignment {
  tableId: string;
  topic?: string;
  partnerId?: string;
  livekitRoom: string;
}

interface NetworkingTokenResponse {
  token: string;
  livekitHost: string;
  room: string;
  assignment: JoinAssignment;
}

interface StartResponse {
  sessionId: string;
  mode: string;
  startsAt: string;
}

interface StopResponse {
  stopped: boolean;
  totalParticipants: number;
  contactsExchanged: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SPEED_DURATION_OPTIONS = [
  { label: "30s", value: 30 },
  { label: "60s", value: 60 },
  { label: "90s", value: 90 },
  { label: "120s", value: 120 },
] as const;

const MODE_LABELS: Record<NetworkingMode, string> = {
  speed: "Speed Networking",
  topics: "Topic Tables",
  open_mixer: "Open Mixer",
};

const MODE_DESCRIPTIONS: Record<NetworkingMode, string> = {
  speed: "Random pairs with timed rotation. Great for meeting many people quickly.",
  topics: "Self-select topic tables. Great for focused discussions.",
  open_mixer: "Free-form mingling. Participants choose who to talk to.",
};

const POLL_INTERVAL_MS = 3000;

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function NetworkingPage() {
  const { webinarId } = useParams();
  const [isHost, setIsHost] = useState(false);
  const [status, setStatus] = useState<NetworkingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine host status (simplified — real check via role)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setIsHost(params.get("role") === "host");
  }, []);

  // Poll status
  useEffect(() => {
    if (!webinarId) return;

    let isMounted = true;

    async function fetchStatus() {
      try {
        const data = await apiFetch<NetworkingStatus>(
          `/v1/webinars/${webinarId}/networking/status`,
        );
        if (isMounted) {
          setStatus(data);
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : "Failed to load networking status");
          setIsLoading(false);
        }
      }
    }

    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [webinarId]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading networking...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-[var(--color-destructive)]">{error}</p>
      </div>
    );
  }

  if (isHost) {
    return <HostControls webinarId={webinarId!} status={status} />;
  }

  return <ParticipantView webinarId={webinarId!} status={status} />;
}

// ---------------------------------------------------------------------------
// Host Controls
// ---------------------------------------------------------------------------

function HostControls({
  webinarId,
  status,
}: {
  webinarId: string;
  status: NetworkingStatus | null;
}) {
  const [selectedMode, setSelectedMode] = useState<NetworkingMode>("speed");
  const [speedDuration, setSpeedDuration] = useState(60);
  const [autoRotate, setAutoRotate] = useState(true);
  const [topics, setTopics] = useState<string[]>(["General Discussion"]);
  const [newTopic, setNewTopic] = useState("");
  const [maxPerTable, setMaxPerTable] = useState(8);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [stopResult, setStopResult] = useState<StopResponse | null>(null);
  const autoRotateRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isActive = status?.active === true;

  // Auto-rotate for speed mode
  useEffect(() => {
    if (!isActive || status?.mode !== "speed" || !autoRotate) {
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
        autoRotateRef.current = null;
      }
      return;
    }

    autoRotateRef.current = setInterval(async () => {
      try {
        await apiFetch(`/v1/webinars/${webinarId}/networking/rotate`, {
          method: "POST",
        });
      } catch {
        // Rotation failed — will retry next interval
      }
    }, speedDuration * 1000);

    return () => {
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
        autoRotateRef.current = null;
      }
    };
  }, [isActive, status?.mode, autoRotate, speedDuration, webinarId]);

  const handleStart = useCallback(async () => {
    setIsStarting(true);
    try {
      await apiFetch<StartResponse>(`/v1/webinars/${webinarId}/networking/start`, {
        method: "POST",
        body: JSON.stringify({
          mode: selectedMode,
          speedDurationSeconds: selectedMode === "speed" ? speedDuration : undefined,
          topics: selectedMode === "topics" ? topics : undefined,
          maxPerTable,
        }),
      });
      setStopResult(null);
    } catch (err) {
      // Error will show via status polling
    } finally {
      setIsStarting(false);
    }
  }, [webinarId, selectedMode, speedDuration, topics, maxPerTable]);

  const handleStop = useCallback(async () => {
    setIsStopping(true);
    try {
      const result = await apiFetch<StopResponse>(
        `/v1/webinars/${webinarId}/networking/stop`,
        { method: "POST" },
      );
      setStopResult(result);
    } catch {
      // Error handled via status
    } finally {
      setIsStopping(false);
    }
  }, [webinarId]);

  const handleRotate = useCallback(async () => {
    setIsRotating(true);
    try {
      await apiFetch(`/v1/webinars/${webinarId}/networking/rotate`, {
        method: "POST",
      });
    } catch {
      // Error handled via status
    } finally {
      setIsRotating(false);
    }
  }, [webinarId]);

  const handleAddTopic = useCallback(() => {
    const trimmed = newTopic.trim();
    if (trimmed && !topics.includes(trimmed)) {
      setTopics((prev) => [...prev, trimmed]);
      setNewTopic("");
    }
  }, [newTopic, topics]);

  const handleRemoveTopic = useCallback((topic: string) => {
    setTopics((prev) => prev.filter((t) => t !== topic));
  }, []);

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <h1 className="text-2xl font-bold text-foreground">Networking Controls</h1>

        {/* Stop result summary */}
        {stopResult && (
          <div className="rounded-lg border border-border bg-muted p-4">
            <h3 className="font-medium text-foreground">Session Ended</h3>
            <div className="mt-2 grid grid-cols-2 gap-4 text-sm text-muted-foreground">
              <div>
                <span className="block text-lg font-bold text-foreground">
                  {stopResult.totalParticipants}
                </span>
                Total Participants
              </div>
              <div>
                <span className="block text-lg font-bold text-foreground">
                  {stopResult.contactsExchanged}
                </span>
                Contacts Exchanged
              </div>
            </div>
          </div>
        )}

        {/* Live dashboard when active */}
        {isActive && (
          <div className="rounded-lg border border-border bg-muted p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 animate-pulse rounded-full bg-[var(--color-success)]/50" />
                <span className="font-medium text-foreground">
                  {MODE_LABELS[(status?.mode as NetworkingMode) ?? "speed"]} Active
                </span>
              </div>
              <span className="text-sm text-muted-foreground">
                {status?.participantCount ?? 0} participants
              </span>
            </div>

            {status?.mode === "speed" && (
              <div className="flex items-center gap-4">
                <div className="text-sm text-muted-foreground">
                  Round {status.currentRound ?? 1}
                </div>
                {status.timeRemaining !== undefined && (
                  <div className="text-sm font-mono text-foreground">
                    {Math.floor(status.timeRemaining / 60)}:
                    {String(status.timeRemaining % 60).padStart(2, "0")}
                  </div>
                )}
                <button
                  onClick={handleRotate}
                  disabled={isRotating}
                  className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-foreground hover:opacity-80 disabled:opacity-50"
                >
                  {isRotating ? "Rotating..." : "Rotate Now"}
                </button>
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={autoRotate}
                    onChange={(e) => setAutoRotate(e.target.checked)}
                    className="rounded"
                  />
                  Auto-rotate
                </label>
              </div>
            )}

            {status?.tables && status.tables.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-foreground">Tables</h4>
                <div className="grid grid-cols-2 gap-2">
                  {status.tables.map((table) => (
                    <div
                      key={table.id}
                      className="rounded-md border border-border bg-background p-2 text-sm"
                    >
                      <div className="font-medium text-foreground">{table.topic}</div>
                      <div className="text-muted-foreground">
                        {table.participantCount}/{table.maxParticipants} participants
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={handleStop}
              disabled={isStopping}
              className="rounded-md bg-[var(--color-destructive)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-destructive)] disabled:opacity-50"
            >
              {isStopping ? "Stopping..." : "End Networking"}
            </button>
          </div>
        )}

        {/* Configuration when not active */}
        {!isActive && (
          <>
            {/* Mode selector */}
            <div className="space-y-3">
              <h2 className="text-lg font-medium text-foreground">Networking Mode</h2>
              <div className="grid grid-cols-3 gap-3">
                {(["speed", "topics", "open_mixer"] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setSelectedMode(mode)}
                    className={`rounded-lg border p-4 text-left transition-colors ${
                      selectedMode === mode
                        ? "border-primary bg-primary/10"
                        : "border-border bg-background hover:bg-muted"
                    }`}
                  >
                    <div className="font-medium text-foreground">{MODE_LABELS[mode]}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {MODE_DESCRIPTIONS[mode]}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Speed config */}
            {selectedMode === "speed" && (
              <div className="space-y-3 rounded-lg border border-border bg-background p-4">
                <h3 className="font-medium text-foreground">Speed Networking Config</h3>
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Duration per round</label>
                  <div className="flex gap-2">
                    {SPEED_DURATION_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setSpeedDuration(opt.value)}
                        className={`rounded-md px-3 py-1.5 text-sm ${
                          speedDuration === opt.value
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-foreground hover:bg-accent"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={autoRotate}
                    onChange={(e) => setAutoRotate(e.target.checked)}
                    className="rounded"
                  />
                  Auto-rotate when timer ends
                </label>
              </div>
            )}

            {/* Topic config */}
            {selectedMode === "topics" && (
              <div className="space-y-3 rounded-lg border border-border bg-background p-4">
                <h3 className="font-medium text-foreground">Topic Tables Config</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTopic}
                    onChange={(e) => setNewTopic(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddTopic()}
                    placeholder="Add a topic..."
                    className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
                  />
                  <button
                    onClick={handleAddTopic}
                    className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {topics.map((topic) => (
                    <span
                      key={topic}
                      className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm text-foreground"
                    >
                      {topic}
                      <button
                        onClick={() => handleRemoveTopic(topic)}
                        className="ml-1 text-muted-foreground hover:text-foreground"
                      >
                        x
                      </button>
                    </span>
                  ))}
                </div>
                <div className="space-y-1">
                  <label className="text-sm text-muted-foreground">Max per table</label>
                  <input
                    type="number"
                    min={2}
                    max={50}
                    value={maxPerTable}
                    onChange={(e) => setMaxPerTable(Number(e.target.value))}
                    className="w-20 rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
                  />
                </div>
              </div>
            )}

            {/* Start button */}
            <button
              onClick={handleStart}
              disabled={isStarting || (selectedMode === "topics" && topics.length === 0)}
              className="rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {isStarting ? "Starting..." : `Start ${MODE_LABELS[selectedMode]}`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Participant View
// ---------------------------------------------------------------------------

function ParticipantView({
  webinarId,
  status,
}: {
  webinarId: string;
  status: NetworkingStatus | null;
}) {
  const [hasJoined, setHasJoined] = useState(false);
  const [assignment, setAssignment] = useState<JoinAssignment | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [exchangeStates, setExchangeStates] = useState<Record<string, "pending" | "mutual">>({});
  const [timerDisplay, setTimerDisplay] = useState<string | null>(null);
  const [roomToken, setRoomToken] = useState<string | null>(null);
  const [livekitHost, setLivekitHost] = useState("");
  const [isConnectingRoom, setIsConnectingRoom] = useState(false);
  const [roomError, setRoomError] = useState<string | null>(null);

  const isActive = status?.active === true;
  const mode = status?.mode as NetworkingMode | null;

  // Timer countdown for speed mode
  useEffect(() => {
    if (!isActive || mode !== "speed" || status?.timeRemaining === undefined) {
      setTimerDisplay(null);
      return;
    }

    let remaining = status.timeRemaining;
    setTimerDisplay(formatTime(remaining));

    const interval = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        setTimerDisplay("0:00");
        clearInterval(interval);
      } else {
        setTimerDisplay(formatTime(remaining));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive, mode, status?.timeRemaining, status?.currentRound]);

  useEffect(() => {
    if (!hasJoined || !webinarId || !isActive) {
      return;
    }

    let isMounted = true;

    async function syncAssignment() {
      try {
        const data = await apiFetch<{ assignment: JoinAssignment | null }>(
          `/v1/webinars/${webinarId}/networking/assignment`,
        );
        if (!isMounted || !data.assignment) {
          return;
        }
        setAssignment((current) => {
          if (
            current?.tableId === data.assignment?.tableId &&
            current?.livekitRoom === data.assignment?.livekitRoom &&
            current?.partnerId === data.assignment?.partnerId &&
            current?.topic === data.assignment?.topic
          ) {
            return current;
          }
          return data.assignment;
        });
      } catch {
        // Keep the current assignment until the next poll.
      }
    }

    void syncAssignment();
    const interval = setInterval(syncAssignment, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [hasJoined, isActive, webinarId]);

  useEffect(() => {
    const waitingForPartner = mode === "speed" && !assignment?.partnerId;
    if (!hasJoined || !assignment?.livekitRoom || waitingForPartner) {
      setRoomToken(null);
      setLivekitHost("");
      setIsConnectingRoom(false);
      setRoomError(null);
      return;
    }

    let isMounted = true;
    setIsConnectingRoom(true);
    setRoomError(null);

    async function fetchRoomToken() {
      try {
        const data = await apiFetch<NetworkingTokenResponse>(
          `/v1/webinars/${webinarId}/networking/token`,
        );
        if (!isMounted) {
          return;
        }
        setRoomToken(data.token);
        setLivekitHost(data.livekitHost);
        setAssignment(data.assignment);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setRoomToken(null);
        setLivekitHost("");
        setRoomError(err instanceof Error ? err.message : "Failed to connect to the networking room");
      } finally {
        if (isMounted) {
          setIsConnectingRoom(false);
        }
      }
    }

    void fetchRoomToken();

    return () => {
      isMounted = false;
    };
  }, [assignment?.livekitRoom, assignment?.partnerId, hasJoined, mode, webinarId]);

  const handleJoin = useCallback(
    async (tableId?: string) => {
      setIsJoining(true);
      try {
        const data = await apiFetch<{ assignment: JoinAssignment }>(
          `/v1/webinars/${webinarId}/networking/join`,
          {
            method: "POST",
            body: JSON.stringify({ tableId }),
          },
        );
        setAssignment(data.assignment);
        setHasJoined(true);
      } catch {
        // Error shown via status polling
      } finally {
        setIsJoining(false);
      }
    },
    [webinarId],
  );

  const handleExchangeContact = useCallback(
    async (targetUserId: string) => {
      try {
        const data = await apiFetch<{
          requested?: boolean;
          exchanged?: boolean;
          contactId?: string;
        }>(`/v1/webinars/${webinarId}/networking/exchange-contact`, {
          method: "POST",
          body: JSON.stringify({ targetUserId }),
        });

        if (data.exchanged) {
          setExchangeStates((prev) => ({ ...prev, [targetUserId]: "mutual" }));
        } else {
          setExchangeStates((prev) => ({ ...prev, [targetUserId]: "pending" }));
        }
      } catch {
        // Silent — will retry
      }
    },
    [webinarId],
  );

  // Not active — show waiting state
  if (!isActive) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center space-y-2">
          <p className="text-lg text-foreground">Networking has not started yet</p>
          <p className="text-sm text-muted-foreground">
            The host will start the networking session shortly.
          </p>
        </div>
      </div>
    );
  }

  // Opt-in gate
  if (!hasJoined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="max-w-md text-center space-y-4">
          <h2 className="text-xl font-bold text-foreground">
            {MODE_LABELS[mode ?? "open_mixer"]}
          </h2>
          <p className="text-muted-foreground">{MODE_DESCRIPTIONS[mode ?? "open_mixer"]}</p>
          <p className="text-sm text-muted-foreground">
            {status?.participantCount ?? 0} people already networking
          </p>

          {/* Topic table selection for topic mode */}
          {mode === "topics" && status?.tables && (
            <div className="space-y-2 text-left">
              <p className="text-sm font-medium text-foreground">Choose a table:</p>
              {status.tables.map((table) => {
                const isFull = table.participantCount >= table.maxParticipants;
                return (
                  <button
                    key={table.id}
                    onClick={() => handleJoin(table.id)}
                    disabled={isJoining || isFull}
                    className={`w-full rounded-lg border p-3 text-left transition-colors ${
                      isFull
                        ? "border-border bg-muted opacity-50"
                        : "border-border bg-background hover:bg-muted"
                    }`}
                  >
                    <div className="font-medium text-foreground">{table.topic}</div>
                    <div className="text-xs text-muted-foreground">
                      {table.participantCount}/{table.maxParticipants} participants
                      {isFull && " (Full)"}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Direct join for speed and open_mixer */}
          {mode !== "topics" && (
            <button
              onClick={() => handleJoin()}
              disabled={isJoining}
              className="rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {isJoining ? "Joining..." : "Join Networking"}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Joined — mode-specific views
  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-2xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-foreground">
            {MODE_LABELS[mode ?? "open_mixer"]}
          </h2>
          {mode === "speed" && timerDisplay && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Round {status?.currentRound ?? 1}
              </span>
              <span
                className={`text-2xl font-mono font-bold ${
                  (status?.timeRemaining ?? 0) <= 10
                    ? "text-[var(--color-destructive)] animate-pulse"
                    : "text-foreground"
                }`}
              >
                {timerDisplay}
              </span>
            </div>
          )}
        </div>

        {/* Speed mode — partner card */}
        {mode === "speed" && (
          <SpeedPartnerCard
            assignment={assignment}
            exchangeStates={exchangeStates}
            onExchangeContact={handleExchangeContact}
          />
        )}

        {/* Topic mode — current table */}
        {mode === "topics" && assignment && (
          <div className="rounded-lg border border-border bg-background p-4 space-y-3">
            <div>
              <div className="text-sm text-muted-foreground">Current Table</div>
              <div className="text-lg font-medium text-foreground">
                {assignment.topic ?? "General"}
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              Room: {assignment.livekitRoom}
            </div>
          </div>
        )}

        {/* Open mixer — participant grid */}
        {mode === "open_mixer" && (
          <div className="rounded-lg border border-border bg-background p-4 space-y-3">
            <div className="text-sm text-muted-foreground">
              You are in the mixer room. Connect with others below.
            </div>
            <div className="text-sm text-foreground">
              Room: {assignment?.livekitRoom ?? "Connecting..."}
            </div>
            <p className="text-xs text-muted-foreground">
              {status?.participantCount ?? 0} participants in this session
            </p>
          </div>
        )}

        {mode === "speed" && !assignment?.partnerId ? (
          <div className="rounded-lg border border-border bg-muted p-8 text-center">
            <p className="text-base font-medium text-foreground">Finding a match...</p>
            <p className="mt-2 text-sm text-muted-foreground">
              You&apos;ll enter room <span className="font-mono text-foreground">{assignment?.livekitRoom ?? "..."}</span>{" "}
              as soon as another participant is paired with you.
            </p>
          </div>
        ) : isConnectingRoom ? (
          <div className="rounded-lg border border-border bg-muted p-8 text-center">
            <p className="text-base font-medium text-foreground">Connecting your networking room...</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Joining <span className="font-mono text-foreground">{assignment?.livekitRoom ?? "..."}</span>
            </p>
          </div>
        ) : roomError ? (
          <div className="rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 p-6 text-center">
            <p className="text-sm font-medium text-[var(--color-destructive)]">Unable to connect to video chat</p>
            <p className="mt-2 text-sm text-muted-foreground">{roomError}</p>
          </div>
        ) : roomToken && livekitHost ? (
          <div className="overflow-hidden rounded-2xl border border-border bg-black">
            <LiveKitRoom
              token={roomToken}
              serverUrl={livekitHost}
              connect={true}
              video={true}
              audio={true}
              data-lk-theme="default"
            >
              <div className="h-[560px]">
                <VideoConference />
                <RoomAudioRenderer />
              </div>
            </LiveKitRoom>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-muted p-8 text-center">
            <p className="text-sm text-muted-foreground">
              Room assignment ready. Waiting to initialize video chat.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Speed Partner Card
// ---------------------------------------------------------------------------

function SpeedPartnerCard({
  assignment,
  exchangeStates,
  onExchangeContact,
}: {
  assignment: JoinAssignment | null;
  exchangeStates: Record<string, "pending" | "mutual">;
  onExchangeContact: (targetUserId: string) => void;
}) {
  const partnerId = assignment?.partnerId;
  const exchangeState = partnerId ? exchangeStates[partnerId] : undefined;

  if (!partnerId) {
    return (
      <div className="rounded-lg border border-border bg-background p-6 text-center">
        <div className="mx-auto mb-3 h-16 w-16 rounded-full bg-muted" />
        <p className="text-muted-foreground">Waiting for a partner...</p>
        <p className="mt-1 text-xs text-muted-foreground">
          You will be paired when another participant joins.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-background p-6 space-y-4">
      <div className="flex items-center gap-4">
        {/* Avatar placeholder */}
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/20 text-lg font-bold text-primary">
          P
        </div>
        <div>
          <div className="font-medium text-foreground">Participant</div>
          <div className="text-sm text-muted-foreground">ID: {partnerId.slice(0, 8)}...</div>
        </div>
      </div>

      {/* Exchange contact button */}
      {exchangeState === "mutual" ? (
        <div className="rounded-md bg-[var(--color-success)]/10 border border-[var(--color-success)]/30 p-3 text-center">
          <p className="text-sm font-medium text-[var(--color-success)]">Contact Exchanged!</p>
          <p className="mt-1 text-xs text-muted-foreground">
            You can now find this contact in your CRM.
          </p>
        </div>
      ) : exchangeState === "pending" ? (
        <div className="rounded-md bg-accent p-3 text-center">
          <p className="text-sm text-muted-foreground">
            Request sent. Waiting for them to exchange too.
          </p>
        </div>
      ) : (
        <button
          onClick={() => onExchangeContact(partnerId)}
          className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Exchange Contact
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}
